/**
 * useResponsePhase - 回應階段 React Hook
 * 管理回應階段的狀態和交互
 */

import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '../../store/gameStore';
import { useResponseStore } from '../../store/responseStore';
import type { ResponseOption, ChainEntry } from '../../types/response';

// ============================================================
// 類型定義
// ============================================================

export interface UseResponsePhaseReturn {
  /** 是否在回應階段 */
  isActive: boolean;
  /** 觸發的卡牌名稱 */
  triggerCardName: string | null;
  /** 觸發者玩家 ID */
  triggerPlayerId: string | null;
  /** 當前擁有優先權的玩家 ID */
  currentPriority: string | null;
  /** 本地玩家 ID */
  localPlayerId: string;
  /** 可用的回應選項 */
  availableResponses: ResponseOption[];
  /** 連鎖堆疊 */
  chainEntries: ChainEntry[];
  /** 剩餘時間（秒） */
  timeRemaining: number | null;
  /** 最大時間（秒） */
  maxTime: number;
  /** 是否輪到本地玩家 */
  isMyTurn: boolean;
  /** 是否在等待 AI */
  waitingForAI: boolean;
  /** 選擇回應 */
  respond: (response: ResponseOption, targets?: string[]) => void;
  /** 放棄回應 */
  pass: () => void;
}

// ============================================================
// Hook 實作
// ============================================================

/**
 * 回應階段 Hook
 * @param overrideLocalPlayerId 覆蓋本地玩家 ID（可選）
 */
export function useResponsePhase(overrideLocalPlayerId?: string): UseResponsePhaseReturn {
  const gameState = useGameStore(state => state.gameState);
  
  // 從 responseStore 取得狀態和 actions
  const phaseState = useResponseStore(state => state.phaseState);
  const chainEntries = useResponseStore(state => state.chainEntries);
  const localPlayerIdFromStore = useResponseStore(state => state.localPlayerId);
  const respondFn = useResponseStore(state => state.respond);
  const passFn = useResponseStore(state => state.pass);

  // 計算剩餘時間
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // 本地玩家 ID（優先使用 override，然後是 store，最後是 gameState）
  const effectiveLocalPlayerId = overrideLocalPlayerId || localPlayerIdFromStore || gameState?.player1.id || '';

  // 更新計時器
  useEffect(() => {
    const startTime = phaseState?.startTime;
    const maxTime = phaseState?.timeRemaining;
    
    if (!phaseState?.isActive || startTime === null || startTime === undefined || maxTime === null || maxTime === undefined) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, maxTime - elapsed);
      setTimeRemaining(remaining / 1000); // 轉換為秒
    };

    updateTimer();
    const interval = setInterval(updateTimer, 100);

    return () => { clearInterval(interval); };
  }, [phaseState?.isActive, phaseState?.startTime, phaseState?.timeRemaining]);

  // 回應處理
  const respond = useCallback((response: ResponseOption, targets?: string[]) => {
    if (gameState) {
      void respondFn(gameState, response, targets);
    }
  }, [gameState, respondFn]);

  // 放棄回應
  const pass = useCallback(() => {
    void passFn();
  }, [passFn]);

  // 計算衍生狀態
  const isActive = phaseState?.isActive ?? false;
  const currentPriority = phaseState?.currentPriority ?? null;
  const isMyTurn = currentPriority === effectiveLocalPlayerId;
  const waitingForAI = phaseState?.waitingForAI ?? false;

  return {
    isActive,
    triggerCardName: phaseState?.triggerCardName ?? null,
    triggerPlayerId: phaseState?.triggerPlayerId ?? null,
    currentPriority,
    localPlayerId: effectiveLocalPlayerId,
    availableResponses: phaseState?.availableResponses ?? [],
    chainEntries,
    timeRemaining,
    maxTime: 5, // 預設 5 秒
    isMyTurn,
    waitingForAI,
    respond,
    pass,
  };
}

/**
 * 簡化版 Hook - 只回傳基本狀態
 */
export function useResponsePhaseStatus(): {
  isActive: boolean;
  isMyTurn: boolean;
  hasResponses: boolean;
} {
  const phaseState = useResponseStore(state => state.phaseState);
  const localPlayerId = useResponseStore(state => state.localPlayerId);

  return {
    isActive: phaseState?.isActive ?? false,
    isMyTurn: phaseState?.currentPriority === localPlayerId,
    hasResponses: (phaseState?.availableResponses.length ?? 0) > 0,
  };
}
